import React, { useMemo, useState } from "react";
import {
	Box,
	Typography,
	Button,
	ButtonGroup,
	Checkbox,
	Chip,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Divider,
	Collapse,
	Alert,
	TextField,
	InputAdornment,
} from "@mui/material";
import { Delete, RestoreFromTrash, Map as MapIcon, ExpandMore, ExpandLess, Search } from "@mui/icons-material";
import PortalTooltip from "../../../../../helpers/PortalTooltip";
import type { GeoTypeSelectionProps } from "./GeoTypeSelection.types";
import { useGeoTypeSelectionViewModel } from "./GeoTypeSelection.vm";
import { getValidGeoTypes, POLYGON_GEO_TYPES, POINT_GEO_TYPES } from "./GeoTypeSelection.types";
import type { GeoType } from "../../WizardContainer.types";
import { GeoPreviewMap } from "../../../../GeoData/GeoPreviewMap/GeoPreviewMap";
import type { GroupedGeoJSON } from "../../../../../../utilities/types";
import "./GeoTypeSelection.css";

/** Display labels for geo-types (some differ from internal values) */
const GEO_TYPE_DISPLAY_LABELS: Record<GeoType, string> = {
	Boundary: "Boundary",
	Zones: "Sample Zones",
	Centroids: "Centroids",
	Cores: "Cores",
};

/** Extract file names from file paths (last segment without extension) */
function getFileNames(filePaths: string[]): string {
	return filePaths
		.map((path) => {
			const lastSegment = path.split("/").pop() || path;
			// Remove common extensions
			return lastSegment.replace(/\.(shp|dbf|shx|prj|cpg|json|geojson|zip|kml|kmz)$/i, "");
		})
		.join(", ");
}

/** Extract directory path from file paths (everything before the last segment) */
function getDirectoryPath(filePaths: string[]): string | null {
	if (filePaths.length === 0) return null;
	const firstPath = filePaths[0];
	const lastSlashIndex = firstPath.lastIndexOf("/");
	if (lastSlashIndex === -1) return null;
	return firstPath.substring(0, lastSlashIndex);
}

/** Get sample values for a property from GeoJSON features (up to maxSamples unique values) */
function getPropertySampleValues(geoJSON: GeoJSON.FeatureCollection, propertyName: string, maxSamples = 3): string {
	const uniqueValues = new Set<string>();
	for (const feature of geoJSON.features) {
		if (uniqueValues.size >= maxSamples) break;
		const value = feature.properties?.[propertyName];
		if (value !== undefined && value !== null) {
			uniqueValues.add(String(value));
		}
	}
	return Array.from(uniqueValues).join(", ");
}

/**
 * GeoTypeSelection step component
 *
 * Allows users to assign geo-types (Boundary/Zone/Centroid/Core) to each
 * geodata group with a two-panel layout showing a map preview.
 */
export default function GeoTypeSelection(props: GeoTypeSelectionProps) {
	const { geodataGroups, geoTypeSelections, selectedGroupIdsForMap } = props;
	const vm = useGeoTypeSelectionViewModel(props);

	// State for expanded groups (details view)
	const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(new Set());

	// State for search filter
	const [searchText, setSearchText] = useState("");

	// Filter active groups by search text
	const filteredGroups = useMemo(() => {
		if (!searchText.trim()) return vm.activeGroups;
		const search = searchText.toLowerCase();
		return vm.activeGroups.filter((group) => group.filePaths.some((path) => path.toLowerCase().includes(search)));
	}, [vm.activeGroups, searchText]);

	// Compute selection state for filtered groups
	const filteredGroupIds = useMemo(() => filteredGroups.map((g) => g.id), [filteredGroups]);
	const selectedFilteredCount = useMemo(
		() => filteredGroupIds.filter((id) => selectedGroupIdsForMap.includes(id)).length,
		[filteredGroupIds, selectedGroupIdsForMap],
	);
	const allFilteredSelected = filteredGroups.length > 0 && selectedFilteredCount === filteredGroups.length;
	const someFilteredSelected = selectedFilteredCount > 0 && selectedFilteredCount < filteredGroups.length;
	const filteredPolygonCount = useMemo(
		() => filteredGroups.filter((g) => g.geometryType === "polygon").length,
		[filteredGroups],
	);
	const filteredPointCount = useMemo(
		() => filteredGroups.filter((g) => g.geometryType === "point").length,
		[filteredGroups],
	);

	// Handler to toggle all filtered groups selection
	const handleToggleSelectAll = () => {
		if (allFilteredSelected) {
			// Deselect all filtered groups
			const newSelection = selectedGroupIdsForMap.filter((id) => !filteredGroupIds.includes(id));
			props.onMapSelectionChange(newSelection);
		} else {
			// Select all filtered groups (add to existing selection)
			const newSelection = [...new Set([...selectedGroupIdsForMap, ...filteredGroupIds])];
			props.onMapSelectionChange(newSelection);
		}
	};

	// Handler to select all polygon groups
	const handleSelectAllPolygons = () => {
		const polygonIds = filteredGroups.filter((g) => g.geometryType === "polygon").map((g) => g.id);
		props.onMapSelectionChange(polygonIds);
	};

	// Handler to select all point groups
	const handleSelectAllPoints = () => {
		const pointIds = filteredGroups.filter((g) => g.geometryType === "point").map((g) => g.id);
		props.onMapSelectionChange(pointIds);
	};

	// Toggle handler for expanding/collapsing group details
	const handleToggleExpanded = (groupId: string) => {
		setExpandedGroupIds((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(groupId)) {
				newSet.delete(groupId);
			} else {
				newSet.add(groupId);
			}
			return newSet;
		});
	};

	// Handler to exclude all selected groups
	const handleExcludeSelected = () => {
		for (const groupId of selectedGroupIdsForMap) {
			vm.handleExcludeGroup(groupId);
		}
	};

	// Convert selected geodata groups to format expected by GeoPreviewMap
	const mapInputData: GroupedGeoJSON[] = useMemo(() => {
		return geodataGroups
			.filter((group) => selectedGroupIdsForMap.includes(group.id))
			.map((group) => ({
				groupKey: group.id,
				geoJSON: group.geoJSON,
				propertyNames: group.propertyNames,
				geometryTypes:
					group.geometryType === "polygon" ? ["Polygon" as const, "MultiPolygon" as const] : ["Point" as const],
			}));
	}, [geodataGroups, selectedGroupIdsForMap]);

	return (
		<Box className="geo-type-selection">
			{/* Header */}
			<Box sx={{ mb: 2 }}>
				<Typography variant="h6" gutterBottom>
					Geo-Type Selection
				</Typography>
				<Typography variant="body2" color="text.secondary">
					Assign a geo-type to each file group. Select groups to preview them on the map.
				</Typography>
			</Box>

			{/* Two-panel layout */}
			<Box className="geo-type-selection__panels">
				{/* Left Panel - Group List */}
				<Box className="geo-type-selection__left-panel">
					{/* Quick Assign Section */}
					<Box sx={{ mb: 2 }}>
						{/* Buttons side-by-side - show "Apply to selected" or "All X are..." based on selection */}
						<Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "flex-start" }}>
							{/* Progress Summary - vertically stacked on the left */}

							{/* Polygon buttons - show selected or all based on selection state */}
							{vm.hasMapSelection && vm.selectedGeometryType === "polygon" ? (
								<Box>
									<Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
										Selected polygons ({selectedGroupIdsForMap.length})...
									</Typography>
									<ButtonGroup size="small" variant="contained" color="primary">
										{POLYGON_GEO_TYPES.map((geoType) => (
											<Button key={geoType} onClick={() => vm.handleApplyToSelected(geoType)}>
												{GEO_TYPE_DISPLAY_LABELS[geoType]}
											</Button>
										))}
									</ButtonGroup>
								</Box>
							) : (
								vm.polygonGroups.length > 0 && (
									<Box>
										<Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
											All polygons are...
										</Typography>
										<ButtonGroup size="small" variant="outlined">
											{POLYGON_GEO_TYPES.map((geoType) => (
												<Button key={geoType} onClick={() => vm.handleApplyToAllPolygons(geoType)}>
													{GEO_TYPE_DISPLAY_LABELS[geoType]}
												</Button>
											))}
										</ButtonGroup>
									</Box>
								)
							)}

							{/* Point buttons - show selected or all based on selection state */}
							{vm.hasMapSelection && vm.selectedGeometryType === "point" ? (
								<Box>
									<Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
										Selected points ({selectedGroupIdsForMap.length})...
									</Typography>
									<ButtonGroup size="small" variant="contained" color="primary">
										{POINT_GEO_TYPES.map((geoType) => (
											<Button key={geoType} onClick={() => vm.handleApplyToSelected(geoType)}>
												{GEO_TYPE_DISPLAY_LABELS[geoType]}
											</Button>
										))}
									</ButtonGroup>
								</Box>
							) : (
								vm.pointGroups.length > 0 && (
									<Box>
										<Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
											All points are...
										</Typography>
										<ButtonGroup size="small" variant="outlined">
											{POINT_GEO_TYPES.map((geoType) => (
												<Button key={geoType} onClick={() => vm.handleApplyToAllPoints(geoType)}>
													{GEO_TYPE_DISPLAY_LABELS[geoType]}
												</Button>
											))}
										</ButtonGroup>
									</Box>
								)
							)}

							{/* Exclude Selected Button */}
							{vm.hasMapSelection && (
								<PortalTooltip title="Exclude selected groups">
									<IconButton size="small" onClick={handleExcludeSelected} sx={{ color: "text.secondary" }}>
										<Delete fontSize="small" />
									</IconButton>
								</PortalTooltip>
							)}
						</Box>
					</Box>

					<Divider sx={{ my: 2 }} />

					{/* Active Groups List */}
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
						<Checkbox
							size="small"
							checked={allFilteredSelected}
							indeterminate={someFilteredSelected}
							onChange={handleToggleSelectAll}
							disabled={filteredGroups.length === 0}
						/>
						<Typography variant="subtitle2">
							Groups ({filteredGroups.length}
							{searchText ? ` of ${vm.totalActiveGroups}` : ""})
						</Typography>
						{filteredPolygonCount > 0 && (
							<PortalTooltip title="Select all polygon groups">
								<Button size="small" variant="text" onClick={handleSelectAllPolygons} sx={{ textTransform: "none" }}>
									Polygon Groups ({filteredPolygonCount})
								</Button>
							</PortalTooltip>
						)}
						{filteredPointCount > 0 && (
							<PortalTooltip title="Select all point groups">
								<Button size="small" variant="text" onClick={handleSelectAllPoints} sx={{ textTransform: "none" }}>
									Point Groups ({filteredPointCount})
								</Button>
							</PortalTooltip>
						)}
						<TextField
							size="small"
							placeholder="Filter by filepath..."
							value={searchText}
							onChange={(e) => setSearchText(e.target.value)}
							sx={{ flex: 1 }}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<Search fontSize="small" sx={{ color: "text.secondary" }} />
									</InputAdornment>
								),
							}}
						/>
					</Box>
					<List dense className="geo-type-selection__group-list">
						{filteredGroups.map((group) => {
							const isSelected = selectedGroupIdsForMap.includes(group.id);
							const isExpanded = expandedGroupIds.has(group.id);
							const geoType = geoTypeSelections[group.id];
							const validGeoTypes = getValidGeoTypes(group.geometryType);

							return (
								<ListItem key={group.id} disablePadding sx={{ flexDirection: "column", alignItems: "stretch" }}>
									<ListItemButton selected={isSelected} onClick={() => handleToggleExpanded(group.id)}>
										<ListItemIcon sx={{ minWidth: 36 }}>
											<Checkbox
												edge="start"
												checked={isSelected}
												tabIndex={-1}
												size="small"
												onClick={(e) => {
													e.stopPropagation();
													vm.handleToggleMapSelection(group.id);
												}}
											/>
										</ListItemIcon>
										<ListItemIcon sx={{ minWidth: 24 }}>
											{isExpanded ? (
												<ExpandLess fontSize="small" sx={{ color: "text.secondary" }} />
											) : (
												<ExpandMore fontSize="small" sx={{ color: "text.secondary" }} />
											)}
										</ListItemIcon>
										<ListItemText
											primary={getFileNames(group.filePaths)}
											secondary={
												getDirectoryPath(group.filePaths) && (
													<Typography
														variant="caption"
														component="span"
														display="block"
														sx={{ color: "text.secondary", wordBreak: "break-word" }}
													>
														{getDirectoryPath(group.filePaths)}
													</Typography>
												)
											}
											primaryTypographyProps={{ variant: "body2", noWrap: true }}
										/>
										<Box
											sx={{ display: "flex", flexDirection: "column", alignItems: "center", ml: 1, flexShrink: 0 }}
											onClick={(e) => e.stopPropagation()}
										>
											<ButtonGroup size="small" orientation="horizontal">
												{validGeoTypes.map((type) => (
													<Button
														key={type}
														variant={geoType === type ? "contained" : "outlined"}
														color={geoType === type ? "primary" : "inherit"}
														onClick={() => vm.handleGeoTypeChange(group.id, type)}
														sx={{
															minWidth: 80,
															px: 1,
															fontSize: "0.7rem",
															whiteSpace: "nowrap",
															...(geoType !== type && {
																borderColor: "divider",
																color: "text.secondary",
															}),
														}}
													>
														{GEO_TYPE_DISPLAY_LABELS[type]}
													</Button>
												))}
												<PortalTooltip title="Exclude from import">
													<Button
														variant="outlined"
														color="inherit"
														onClick={() => vm.handleExcludeGroup(group.id)}
														sx={{
															minWidth: "unset",
															px: 1,
															borderColor: "divider",
															color: "text.secondary",
														}}
													>
														<Delete fontSize="small" />
													</Button>
												</PortalTooltip>
											</ButtonGroup>
											<Typography variant="caption" sx={{ color: "text.secondary", mt: 0.5 }}>
												{`${group.featureCount} ${group.geometryType === "polygon" ? "polygons" : "points"}`}
											</Typography>
										</Box>
									</ListItemButton>
									<Collapse in={isExpanded} timeout="auto" unmountOnExit>
										<Box sx={{ p: 2, pl: 9, borderTop: 1, borderColor: "divider", bgcolor: "grey.50" }}>
											{/* File Paths */}
											<Typography variant="caption" color="text.secondary" fontWeight="bold">
												File Paths
											</Typography>
											{group.filePaths.map((path, idx) => (
												<Typography key={idx} variant="caption" display="block" sx={{ pl: 1 }}>
													{path}
												</Typography>
											))}

											{/* Properties */}
											{(() => {
												const filteredProps = group.propertyNames.filter(
													(prop) => prop !== "FilePath" && !/^FolderLevel\d+$/i.test(prop),
												);
												return (
													<>
														<Typography
															variant="caption"
															color="text.secondary"
															fontWeight="bold"
															sx={{ mt: 1, display: "block" }}
														>
															Properties ({filteredProps.length})
														</Typography>
														<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, pl: 1, mt: 0.5 }}>
															{filteredProps.map((prop) => {
																const sampleValues = getPropertySampleValues(group.geoJSON, prop);
																return (
																	<Chip
																		key={prop}
																		label={
																			<span>
																				<strong>{prop}</strong>
																				{sampleValues ? `: ${sampleValues}` : null}
																			</span>
																		}
																		size="small"
																		variant="outlined"
																	/>
																);
															})}
														</Box>
													</>
												);
											})()}
										</Box>
									</Collapse>
								</ListItem>
							);
						})}
					</List>

					{/* Excluded Groups Section */}
					{vm.excludedGroups.length > 0 && (
						<>
							<Divider sx={{ my: 2 }} />
							<Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
								Excluded ({vm.totalExcludedGroups})
							</Typography>
							<List dense className="geo-type-selection__excluded-list">
								{vm.excludedGroups.map((group) => (
									<ListItem key={group.id} disablePadding sx={{ display: "flex", gap: 1, px: 2, py: 0.5 }}>
										<ListItemText
											primary={getFileNames(group.filePaths)}
											secondary={
												getDirectoryPath(group.filePaths) && (
													<Typography
														variant="caption"
														component="span"
														display="block"
														sx={{ color: "text.secondary" }}
														noWrap
													>
														{getDirectoryPath(group.filePaths)}
													</Typography>
												)
											}
											sx={{ flex: 1, minWidth: 0, opacity: 0.6 }}
											primaryTypographyProps={{ variant: "body2", noWrap: true }}
										/>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
											<Typography
												variant="caption"
												sx={{ color: "text.secondary", whiteSpace: "nowrap", opacity: 0.6 }}
											>
												{`${group.featureCount} ${group.geometryType === "polygon" ? "polygons" : "points"}`}
											</Typography>
											<PortalTooltip title="Include in import">
												<IconButton size="small" onClick={() => vm.handleIncludeGroup(group.id)}>
													<RestoreFromTrash fontSize="small" />
												</IconButton>
											</PortalTooltip>
										</Box>
									</ListItem>
								))}
							</List>
						</>
					)}

					{/* Empty state */}
					{geodataGroups.length === 0 && (
						<Alert severity="info">No geodata groups available. Go back and upload files.</Alert>
					)}
				</Box>

				{/* Right Panel - Map Preview */}
				<Box className="geo-type-selection__right-panel">
					<Box className="geo-type-selection__map-container">
						{vm.hasMapSelection && mapInputData.length > 0 ? (
							<GeoPreviewMap inputData={mapInputData} groupIndex={0} />
						) : (
							<Box
								sx={{
									height: "100%",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									flexDirection: "column",
									gap: 1,
								}}
							>
								<MapIcon sx={{ fontSize: 48, color: "text.disabled" }} />
								<Typography variant="body2" color="text.secondary">
									Select groups to preview on map
								</Typography>
							</Box>
						)}
					</Box>
				</Box>
			</Box>
		</Box>
	);
}
